/**
 * HomePage - Welcome page without authentication
 * Shows logo, invitation to try the app, and download guide
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Description as DocIcon,
  School as SchoolIcon,
  TrendingUp as TrendingIcon,
  Login as LoginIcon
} from '@mui/icons-material';

interface HomePageProps {
  onStartProject: () => void;
  onShowContacts: () => void;
  onShowLogin: () => void;
  onShowInfo?: () => void;
  onShowProjects?: () => void;
  onShowInsights?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onStartProject, onShowContacts, onShowLogin, onShowInfo, onShowProjects, onShowInsights }) => {
  const handleDownloadGuide = () => {
    // Download the PDF guide
    window.open('/ai-erasmus-guide-marinuzzi.pdf', '_blank');
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)' }}>
      {/* Header with Logo and Login Button */}
      <AppBar position="static" sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ justifyContent: 'space-between', pt: 2 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Erasmus+ Logo"
            sx={{
              height: 60,
              maxWidth: '100%',
              objectFit: 'contain'
            }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            {onShowProjects && (
              <Button
                onClick={onShowProjects}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Projects
              </Button>
            )}
            {onShowInsights && (
              <Button
                onClick={onShowInsights}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Insights
              </Button>
            )}
            {onShowInfo && (
              <Button
                onClick={onShowInfo}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Info
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              onClick={onShowLogin}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Main Hero Section */}
        <Paper
          elevation={8}
          sx={{
            p: 6,
            mb: 6,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.98)',
            borderRadius: 4
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#003399',
              mb: 2
            }}
          >
            Erasmus+ Project Generator
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
          >
            Experience the power of AI-driven project generation. Enter your project idea without commitment and discover the intelligent capabilities of our platform.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AIIcon />}
              onClick={onStartProject}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                bgcolor: '#003399',
                '&:hover': { bgcolor: '#002266' }
              }}
            >
              Start Your Project
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<DocIcon />}
              onClick={handleDownloadGuide}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                borderColor: '#003399',
                color: '#003399',
                '&:hover': { borderColor: '#002266', bgcolor: 'rgba(0, 51, 153, 0.1)' }
              }}
            >
              Download AI Guide (PDF)
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No registration required to explore. Try it now and see what AI can do for your Erasmus+ project!
          </Typography>
        </Paper>

        {/* Features Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <AIIcon sx={{ fontSize: 60, color: '#003399', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  AI-Powered Generation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Intelligent content generation for all sections of your Erasmus+ application using advanced AI technology.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <SchoolIcon sx={{ fontSize: 60, color: '#003399', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Professional Formatting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically formatted documents following EU/Erasmus+ standards and best practices.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', textAlign: 'center' }}>
              <CardContent>
                <TrendingIcon sx={{ fontSize: 60, color: '#003399', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Smart Suggestions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get intelligent suggestions for objectives, work packages, and project structure.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* How It Works */}
        <Paper sx={{ p: 4, background: 'rgba(255, 255, 255, 0.98)', borderRadius: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', color: '#003399', mb: 4 }}>
            How It Works
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#003399',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  1
                </Box>
                <Typography variant="h6" gutterBottom>
                  Enter Your Idea
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start with your project concept - no commitment required
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#003399',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  2
                </Box>
                <Typography variant="h6" gutterBottom>
                  Fill Project Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete basic information, content, and work packages
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#003399',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  3
                </Box>
                <Typography variant="h6" gutterBottom>
                  Generate & Review
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI generates your complete application document
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#003399',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  4
                </Box>
                <Typography variant="h6" gutterBottom>
                  Export & Submit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Download your professional document ready for submission
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

      </Container>
    </Box>
  );
};

export default HomePage;

